import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { fondosAPI, cuentasBancariasAPI, cajasAPI, formasPagoAPI, usuariosAPI } from '../services/api';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import { useZonaHoraria } from '../context/ZonaHorariaContext';
import '../Movimientos.css';
import '../Clientes.css';
import '../MovimientosFondos.css';
import { sortFondosCronologico, deltaSaldoFondo } from '../utils/movimientosOrden';
import { imprimirResumenMovimientoFondo } from '../utils/impresionMovimientos';
import { etiquetaClaseFondo, textoOrigenDestino } from '../utils/fondosLabels';
import { SelectorColumnas } from '../components/SelectorColumnas';
import { SelectorFiltros } from '../components/SelectorFiltros';
import { FormasPagoDetalleList } from '../components/FormasPagoDetalleList';
import { ExportActions } from '../components/ExportActions';
import { exportFromColumnas, valorCeldaFondosExport, imprimirListadoColumnas } from '../utils/exportListado';
import { useBranding } from '../context/BrandingContext';
import { loadFiltrosSession, saveFiltrosSession } from '../utils/filtrosSession';

const CLAVE_PREFERENCIAS = 'movimientos_fondos_listado';
const CLAVE_FILTROS = 'movimientos_fondos_listado_filtros';

const COLUMNAS_DEFINICION = [
  { id: 'fecha', label: 'Fecha' },
  { id: 'cuenta_banco', label: 'Origen → Destino' },
  { id: 'tipo', label: 'Tipo' },
  { id: 'concepto', label: 'Concepto' },
  { id: 'ingreso', label: 'Ingreso' },
  { id: 'egreso', label: 'Egreso' },
  { id: 'saldo', label: 'Saldo acumulado' },
  { id: 'medio_pago', label: 'Medio de pago' },
  { id: 'usuario', label: 'Usuario' },
  { id: 'detalles', label: 'Detalle' },
];

const FILTROS_DEFINICION = [
  { id: 'fecha', label: 'Fecha (desde / hasta)' },
  { id: 'cuenta', label: 'Cuenta / Caja' },
  { id: 'tipo', label: 'Tipo (ingreso / egreso)' },
  { id: 'medio_pago', label: 'Medio de pago' },
  { id: 'usuario', label: 'Usuario' },
];

const defaultColumnasVisibles = COLUMNAS_DEFINICION.reduce((acc, c) => ({ ...acc, [c.id]: true }), {});
const defaultFiltrosVisibles = FILTROS_DEFINICION.reduce((acc, f) => ({ ...acc, [f.id]: true }), {});
const defaultOrdenColumnas = COLUMNAS_DEFINICION.map(c => c.id);
const defaultFiltrosValores = {
  tipo: '',
  cuenta_o_caja: [], // ['caja_1', 'cuenta_2', ...] selección múltiple
  fecha_desde: '',
  fecha_hasta: '',
  forma_pago_id: '',
  usuario_id: '',
};

// Etiqueta de medio de pago: nombre de forma de pago o desde detalles
const getMedioPagoLabel = (mf) => {
  if (mf.forma_pago_nombre) return mf.forma_pago_nombre;
  const detalles = mf.detalles || [];
  if (!detalles.length) return '-';
  const tipos = [...new Set(detalles.map(d => d.tipo))];
  const labels = tipos.map(t => {
    if (t === 'caja') return 'Caja';
    if (t === 'cuenta') return 'Cuenta';
    if (t === 'cheque_tercero') return 'Cheque de tercero';
    return t;
  });
  return labels.join(', ');
};

const getSortValueFondos = (mf, colId) => {
  switch (colId) {
    case 'fecha':
      return new Date(mf.fecha || 0).getTime() || 0;
    case 'cuenta_banco':
      return textoOrigenDestino(mf).toLowerCase();
    case 'tipo':
      return (mf.tipo || '').toString().toLowerCase();
    case 'concepto':
      return (mf.concepto || '').toString().toLowerCase();
    case 'ingreso':
      return mf.tipo === 'ingreso' ? (parseFloat(mf.monto_total) || 0) : 0;
    case 'egreso':
      return mf.tipo === 'egreso' ? (parseFloat(mf.monto_total) || 0) : 0;
    case 'saldo':
      return parseFloat(mf.saldo_acumulado || 0) || 0;
    case 'medio_pago':
      return getMedioPagoLabel(mf).toLowerCase();
    case 'usuario':
      return (mf.usuario_nombre || '').toString().toLowerCase();
    case 'detalles':
      return (mf.detalles && mf.detalles.length) || 0;
    default:
      return '';
  }
};

const MovimientosFondos = () => {
  const { formatFecha } = useZonaHoraria();
  const [searchParams] = useSearchParams();
  const cajaIdParam = searchParams.get('caja_id');
  const cuentaIdParam = searchParams.get('cuenta_id');
  const [movimientos, setMovimientos] = useState([]);
  const [cuentasBancarias, setCuentasBancarias] = useState([]);
  const [cajas, setCajas] = useState([]);
  const [formasPago, setFormasPago] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refrescando, setRefrescando] = useState(false);
  const primeraCargaRef = useRef(true);
  const cargaSeqRef = useRef(0);
  const [detalleFondo, setDetalleFondo] = useState(null);
  const [detalleFondoLoading, setDetalleFondoLoading] = useState(false);
  const [detalleFondoError, setDetalleFondoError] = useState(null);
  const [filtrosValores, setFiltrosValores] = useState(() => {
    const base = loadFiltrosSession(CLAVE_FILTROS, defaultFiltrosValores);
    try {
      const sp = new URLSearchParams(window.location.search);
      const caja = sp.get('caja_id');
      const cuenta = sp.get('cuenta_id');
      if (caja) base.cuenta_o_caja = [`caja_${caja}`];
      else if (cuenta) base.cuenta_o_caja = [`cuenta_${cuenta}`];
    } catch (_) { /* ignore */ }
    return base;
  });
  const [columnasVisibles, setColumnasVisibles] = useState(() => ({ ...defaultColumnasVisibles }));
  const [filtrosVisibles, setFiltrosVisibles] = useState(() => ({ ...defaultFiltrosVisibles }));
  const [ordenColumnas, setOrdenColumnas] = useState(() => [...defaultOrdenColumnas]);
  const [sortByColumn, setSortByColumn] = useState('fecha');
  const [sortAsc, setSortAsc] = useState(false);
  const [, setLoadingPreferencias] = useState(true);
  const [savingPreferencias, setSavingPreferencias] = useState(false);
  const [abiertoFiltroCuentaCaja, setAbiertoFiltroCuentaCaja] = useState(false);
  const [saldoFiltrado, setSaldoFiltrado] = useState(null);
  const [saldoCuenta, setSaldoCuenta] = useState(null);
  const { hasPermission } = useAuth();
  const { logoUrl } = useBranding();

  useEffect(() => {
    saveFiltrosSession(CLAVE_FILTROS, filtrosValores);
  }, [filtrosValores]);

  useEffect(() => {
    if (!cajaIdParam && !cuentaIdParam) return;
    setFiltrosValores((prev) => {
      const nextKey = cajaIdParam ? `caja_${cajaIdParam}` : `cuenta_${cuentaIdParam}`;
      const prevSel = Array.isArray(prev.cuenta_o_caja) ? prev.cuenta_o_caja : [];
      if (prevSel.length === 1 && prevSel[0] === nextKey) return prev;
      return { ...prev, cuenta_o_caja: [nextKey] };
    });
  }, [cajaIdParam, cuentaIdParam]);

  const tituloEntidadFiltro = useMemo(() => {
    if (cajaIdParam) {
      const c = cajas.find((x) => String(x.id) === String(cajaIdParam));
      return c ? `Caja: ${c.nombre}` : null;
    }
    if (cuentaIdParam) {
      const c = cuentasBancarias.find((x) => String(x.id) === String(cuentaIdParam));
      return c ? `Cuenta: ${c.banco_nombre || ''} ${c.numero_cuenta || ''}`.trim() : null;
    }
    return null;
  }, [cajaIdParam, cuentaIdParam, cajas, cuentasBancarias]);

  const colVisible = (id) => columnasVisibles[id] !== false;
  const filtroVisible = useCallback((id) => filtrosVisibles[id] !== false, [filtrosVisibles]);
  const setFiltro = (key, value) => setFiltrosValores(prev => ({ ...prev, [key]: value }));

  const toggleCuentaOCaja = (valor) => {
    const arr = Array.isArray(filtrosValores.cuenta_o_caja) ? filtrosValores.cuenta_o_caja : [];
    const set = new Set(arr);
    if (set.has(valor)) set.delete(valor);
    else set.add(valor);
    setFiltro('cuenta_o_caja', [...set]);
  };

  const loadCuentasBancarias = useCallback(async () => {
    try {
      const res = await cuentasBancariasAPI.getAll({ activo: true });
      if (res.success && Array.isArray(res.data)) setCuentasBancarias(res.data);
      else setCuentasBancarias([]);
    } catch (e) {
      setCuentasBancarias([]);
    }
  }, []);

  const loadCajas = useCallback(async () => {
    try {
      const res = await cajasAPI.getAll({ activo: true });
      if (res.success && Array.isArray(res.data)) setCajas(res.data);
      else setCajas([]);
    } catch (e) {
      setCajas([]);
    }
  }, []);

  const loadFormasPago = useCallback(async () => {
    try {
      const res = await formasPagoAPI.getAll({ activo: true });
      if (res.success && Array.isArray(res.data)) setFormasPago(res.data);
      else setFormasPago([]);
    } catch (e) {
      setFormasPago([]);
    }
  }, []);

  const loadUsuarios = useCallback(async () => {
    try {
      const res = await usuariosAPI.getAll();
      if (res.success && Array.isArray(res.data)) setUsuarios(res.data);
      else setUsuarios([]);
    } catch (e) {
      setUsuarios([]);
    }
  }, []);

  const loadMovimientos = useCallback(async () => {
    const mostrarLoaderPantalla = primeraCargaRef.current;
    const seq = ++cargaSeqRef.current;
    try {
      if (mostrarLoaderPantalla) setLoading(true);
      else setRefrescando(true);
      const dateParams = {};
      if (filtroVisible('fecha') && filtrosValores.fecha_desde?.trim()) {
        dateParams.fecha_desde = filtrosValores.fecha_desde.trim();
      }
      if (filtroVisible('fecha') && filtrosValores.fecha_hasta?.trim()) {
        dateParams.fecha_hasta = filtrosValores.fecha_hasta.trim();
      }

      let cajaIds = [];
      let cuentaIds = [];
      if (filtroVisible('cuenta')) {
        const sel = Array.isArray(filtrosValores.cuenta_o_caja) ? filtrosValores.cuenta_o_caja : [];
        cajaIds = sel.filter(v => String(v).startsWith('caja_')).map(v => String(v).replace('caja_', ''));
        cuentaIds = sel.filter(v => String(v).startsWith('cuenta_')).map(v => String(v).replace('cuenta_', ''));
      }

      const filtroUnicoCaja = cajaIds.length === 1 && cuentaIds.length === 0;
      const filtroUnicoCuenta = cuentaIds.length === 1 && cajaIds.length === 0;

      const aplicarFiltrosLocales = (lista) => {
        let data = lista;
        if (filtroVisible('tipo') && filtrosValores.tipo) {
          data = data.filter(m => m.tipo === filtrosValores.tipo);
        }
        if (filtroVisible('medio_pago') && filtrosValores.forma_pago_id) {
          data = data.filter(m => String(m.forma_pago_id || '') === String(filtrosValores.forma_pago_id));
        }
        if (filtroVisible('usuario') && filtrosValores.usuario_id) {
          data = data.filter(m => String(m.usuario_id || '') === String(filtrosValores.usuario_id));
        }
        return data;
      };

      if (filtroUnicoCaja) {
        const res = await fondosAPI.getMovimientosCaja(cajaIds[0], dateParams);
        if (seq !== cargaSeqRef.current) return;
        if (res.success && Array.isArray(res.data)) {
          setMovimientos(aplicarFiltrosLocales(res.data));
          setSaldoFiltrado(res.saldo_inicial !== undefined ? res.saldo_inicial : null);
          setSaldoCuenta(res.saldo_cuenta !== undefined ? res.saldo_cuenta : null);
        } else if (mostrarLoaderPantalla) {
          setMovimientos([]);
          setSaldoFiltrado(null);
          setSaldoCuenta(null);
        }
        return;
      }

      if (filtroUnicoCuenta) {
        const res = await fondosAPI.getMovimientosCuenta(cuentaIds[0], dateParams);
        if (seq !== cargaSeqRef.current) return;
        if (res.success && Array.isArray(res.data)) {
          setMovimientos(aplicarFiltrosLocales(res.data));
          setSaldoFiltrado(res.saldo_inicial !== undefined ? res.saldo_inicial : null);
          setSaldoCuenta(res.saldo_cuenta !== undefined ? res.saldo_cuenta : null);
        } else if (mostrarLoaderPantalla) {
          setMovimientos([]);
          setSaldoFiltrado(null);
          setSaldoCuenta(null);
        }
        return;
      }

      const params = { ...dateParams, limit: 0 };
      if (cajaIds.length) params.caja_ids = cajaIds.join(',');
      if (cuentaIds.length) params.cuenta_bancaria_ids = cuentaIds.join(',');
      if (filtroVisible('tipo') && filtrosValores.tipo) params.tipo = filtrosValores.tipo;
      if (filtroVisible('medio_pago') && filtrosValores.forma_pago_id) params.forma_pago_id = filtrosValores.forma_pago_id;
      if (filtroVisible('usuario') && filtrosValores.usuario_id) params.usuario_id = filtrosValores.usuario_id;
      const hayFiltros =
        cajaIds.length > 0 ||
        cuentaIds.length > 0 ||
        (filtroVisible('tipo') && filtrosValores.tipo) ||
        (filtroVisible('fecha') && (filtrosValores.fecha_desde?.trim() || filtrosValores.fecha_hasta?.trim())) ||
        (filtroVisible('medio_pago') && filtrosValores.forma_pago_id) ||
        (filtroVisible('usuario') && filtrosValores.usuario_id);
      if (hayFiltros) params.incluir_resumen_saldos = '1';
      const res = await fondosAPI.getAll(params);
      if (seq !== cargaSeqRef.current) return;
      if (res.success && Array.isArray(res.data)) {
        setMovimientos(res.data);
        setSaldoFiltrado(res.saldo_filtrado !== undefined ? res.saldo_filtrado : null);
        setSaldoCuenta(res.saldo_cuenta !== undefined ? res.saldo_cuenta : null);
      } else if (mostrarLoaderPantalla) {
        setMovimientos([]);
        setSaldoFiltrado(null);
        setSaldoCuenta(null);
      }
    } catch (error) {
      if (seq !== cargaSeqRef.current) return;
      toast.error(error.message || 'Error al cargar movimientos de fondos');
      if (mostrarLoaderPantalla) {
        setMovimientos([]);
        setSaldoFiltrado(null);
        setSaldoCuenta(null);
      }
    } finally {
      if (mostrarLoaderPantalla) {
        setLoading(false);
        primeraCargaRef.current = false;
      }
      if (seq === cargaSeqRef.current) setRefrescando(false);
    }
  }, [filtrosValores, filtroVisible]);

  const cerrarDetalleFondo = useCallback(() => {
    setDetalleFondo(null);
    setDetalleFondoError(null);
    setDetalleFondoLoading(false);
  }, []);

  const abrirDetalleFondo = useCallback(async (mf) => {
    setDetalleFondo(mf);
    setDetalleFondoError(null);
    setDetalleFondoLoading(true);
    try {
      const res = await fondosAPI.getById(mf.id);
      if (res.success && res.data) {
        setDetalleFondo(res.data);
      } else {
        setDetalleFondoError(res.message || 'No se pudo cargar el detalle completo');
      }
    } catch (e) {
      setDetalleFondoError(e.message || 'No se pudo cargar el detalle completo');
    } finally {
      setDetalleFondoLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCuentasBancarias();
    loadCajas();
    loadFormasPago();
    loadUsuarios();
  }, [loadCuentasBancarias, loadCajas, loadFormasPago, loadUsuarios]);

  useEffect(() => {
    loadMovimientos();
  }, [loadMovimientos]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingPreferencias(true);
      try {
        const res = await usuariosAPI.getPreferencias(CLAVE_PREFERENCIAS);
        if (cancelled) return;
        const raw = Array.isArray(res.data) ? res.data[0] : res.data;
        const data = raw?.valor ?? null;
        if (data && typeof data === 'object') {
          if (data.columnasVisibles && typeof data.columnasVisibles === 'object') {
            setColumnasVisibles(prev => ({ ...defaultColumnasVisibles, ...data.columnasVisibles }));
          }
          if (data.filtrosVisibles && typeof data.filtrosVisibles === 'object') {
            setFiltrosVisibles(prev => ({ ...defaultFiltrosVisibles, ...data.filtrosVisibles }));
          }
          if (data.ordenColumnas && Array.isArray(data.ordenColumnas) && data.ordenColumnas.length) {
            setOrdenColumnas(data.ordenColumnas);
          }
        }
      } catch (e) {
        if (!cancelled) console.error('Error al cargar preferencias:', e);
      } finally {
        if (!cancelled) setLoadingPreferencias(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const guardarPreferenciasListado = useCallback(async () => {
    setSavingPreferencias(true);
    try {
      await usuariosAPI.savePreferencias(CLAVE_PREFERENCIAS, {
        columnasVisibles: { ...columnasVisibles },
        filtrosVisibles: { ...filtrosVisibles },
        ordenColumnas: [...ordenColumnas],
      });
      toast.success('Configuración guardada.');
    } catch (e) {
      toast.error(e.message || 'Error al guardar configuración');
    } finally {
      setSavingPreferencias(false);
    }
  }, [columnasVisibles, filtrosVisibles, ordenColumnas]);

  const ordenColumnasVisible = useMemo(
    () => ordenColumnas.filter(id => colVisible(id)),
    [ordenColumnas, columnasVisibles]
  );

  const hayFiltrosActivos = useMemo(() => {
    return (
      (filtroVisible('cuenta') && Array.isArray(filtrosValores.cuenta_o_caja) && filtrosValores.cuenta_o_caja.length > 0) ||
      (filtroVisible('tipo') && filtrosValores.tipo) ||
      (filtroVisible('fecha') && (filtrosValores.fecha_desde?.trim() || filtrosValores.fecha_hasta?.trim())) ||
      (filtroVisible('medio_pago') && filtrosValores.forma_pago_id) ||
      (filtroVisible('usuario') && filtrosValores.usuario_id)
    );
  }, [filtrosValores, filtroVisible]);

  const extractoEntidadUnica = useMemo(() => {
    if (!filtroVisible('cuenta')) return false;
    const sel = Array.isArray(filtrosValores.cuenta_o_caja) ? filtrosValores.cuenta_o_caja : [];
    const cajas = sel.filter((v) => String(v).startsWith('caja_'));
    const cuentas = sel.filter((v) => String(v).startsWith('cuenta_'));
    return (cajas.length === 1 && cuentas.length === 0) || (cuentas.length === 1 && cajas.length === 0);
  }, [filtrosValores.cuenta_o_caja, filtroVisible]);

  const movimientosOrdenados = useMemo(() => {
    // Por defecto (o sin columna): fecha descendente (más nueva → más vieja)
    if (!sortByColumn) {
      return [...movimientos].sort((a, b) => {
        const cmp = sortFondosCronologico(a, b);
        return -cmp;
      });
    }
    if (sortByColumn === 'saldo') {
      const porFecha = [...movimientos].sort((a, b) => new Date(a.fecha) - new Date(b.fecha) || (a.id - b.id));
      let s = 0;
      const conSaldo = porFecha.map(m => {
        s += m.tipo === 'ingreso' ? (parseFloat(m.monto_total) || 0) : -(parseFloat(m.monto_total) || 0);
        return { ...m, saldo_acumulado: s };
      });
      conSaldo.sort((a, b) => {
        const v = (parseFloat(a.saldo_acumulado) || 0) - (parseFloat(b.saldo_acumulado) || 0);
        return sortAsc ? v : -v;
      });
      return conSaldo;
    }
    const arr = [...movimientos];
    arr.sort((a, b) => {
      const v1 = getSortValueFondos(a, sortByColumn);
      const v2 = getSortValueFondos(b, sortByColumn);
      const num = typeof v1 === 'number' && typeof v2 === 'number';
      let cmp = 0;
      if (num) cmp = v1 - v2;
      else cmp = String(v1).localeCompare(String(v2), 'es');
      if (cmp === 0 && sortByColumn === 'fecha') {
        cmp = (a.id || 0) - (b.id || 0);
      }
      return sortAsc ? cmp : -cmp;
    });
    return arr;
  }, [movimientos, sortByColumn, sortAsc]);

  // Saldo acumulado: si el backend ya lo calculó (vista por caja/cuenta), usarlo; si no, calcular localmente
  const movimientosConSaldoAcumulado = useMemo(() => {
    const saldoDesdeApi = movimientos.some((m) => m.saldo_acumulado != null && m.saldo_acumulado !== '');
    if (saldoDesdeApi) {
      return movimientosOrdenados.map((m) => ({
        ...m,
        saldo_acumulado: parseFloat(m.saldo_acumulado) || 0,
      }));
    }
    const porFecha = [...movimientos].sort(sortFondosCronologico);
    let saldo = saldoFiltrado != null ? parseFloat(saldoFiltrado) || 0 : 0;
    const saldoPorId = {};
    porFecha.forEach((m) => {
      saldo += deltaSaldoFondo(m);
      saldoPorId[m.id] = saldo;
    });
    return movimientosOrdenados.map((m) => ({ ...m, saldo_acumulado: saldoPorId[m.id] ?? 0 }));
  }, [movimientosOrdenados, movimientos, saldoFiltrado]);

  const handleSortColumn = (columnId) => {
    if (sortByColumn === columnId) {
      setSortAsc(prev => !prev);
    } else {
      setSortByColumn(columnId);
      setSortAsc(true);
    }
  };

  const getColumnLabel = (columnId) =>
    COLUMNAS_DEFINICION.find(c => c.id === columnId)?.label || columnId;

  const limpiarFiltros = () => {
    setFiltrosValores({ ...defaultFiltrosValores });
  };

  const handleExportListado = async (format) => {
    try {
      await exportFromColumnas({
        filename: tituloEntidadFiltro ? `movimientos_fondos_${tituloEntidadFiltro}` : 'movimientos_fondos',
        sheetName: 'Movimientos',
        columnDefs: COLUMNAS_DEFINICION,
        visibleColumnIds: ordenColumnasVisible,
        items: movimientosConSaldoAcumulado,
        getCellValue: (mf, colId) => valorCeldaFondosExport(mf, colId, formatFecha),
        format,
      });
    } catch (e) {
      toast.error(e.message || 'Error al exportar');
    }
  };

  const generarPDFListado = () => {
    if (!movimientosConSaldoAcumulado.length) {
      toast.info('No hay movimientos para imprimir');
      return;
    }
    try {
      const subtituloParts = [];
      if (filtrosValores.fecha_desde || filtrosValores.fecha_hasta) {
        subtituloParts.push(`Período: ${filtrosValores.fecha_desde || '...'} a ${filtrosValores.fecha_hasta || '...'}`);
      }
      if (saldoFiltrado != null) {
        subtituloParts.push(`Saldo filtrado: $${Number(saldoFiltrado).toLocaleString('es-AR', { minimumFractionDigits: 2 })}`);
      }
      subtituloParts.push(`Generado el ${formatFecha(new Date())}`);
      imprimirListadoColumnas({
        titulo: tituloEntidadFiltro ? `Movimientos — ${tituloEntidadFiltro}` : 'Movimientos de fondos',
        subtitulo: subtituloParts.join(' · '),
        columnDefs: COLUMNAS_DEFINICION,
        visibleColumnIds: ordenColumnasVisible,
        items: movimientosConSaldoAcumulado,
        getCellValue: (mf, colId) => valorCeldaFondosExport(mf, colId, formatFecha),
        logoUrl,
      });
    } catch (e) {
      toast.error(e.message || 'Error al imprimir');
    }
  };

  return (
    <div className="movimientos movimientos-fondos-page pagina-listado pagina-listado--scroll">
      <div className="page-header movimientos-fondos-header">
        <div>
          <h1>{tituloEntidadFiltro ? `Movimientos — ${tituloEntidadFiltro}` : 'Movimientos de fondos'}</h1>
          <p className="page-subtitle">
            {tituloEntidadFiltro ? (
              <>
                <Link to="/fondos">← Lista de fondos</Link>
                {' · '}
                Depósitos, extracciones y transferencias del fondo seleccionado.
              </>
            ) : (
              'Depósitos, extracciones y transferencias entre cajas y cuentas bancarias.'
            )}
          </p>
        </div>
      </div>

      <div className="listado-panel clientes-listado listado-controles movimientos-fondos-listado">
      <div className="filters" style={{ flexWrap: 'wrap', gap: '8px 12px', alignItems: 'center' }}>
        {filtroVisible('fecha') && (
          <>
            <label style={{ fontSize: '12px', color: '#666', marginRight: 2 }}>Fecha desde</label>
            <input
              type="date"
              value={filtrosValores.fecha_desde}
              onChange={(e) => setFiltro('fecha_desde', e.target.value)}
              placeholder="Desde"
            />
            <label style={{ fontSize: '12px', color: '#666', marginRight: 2, marginLeft: 8 }}>hasta</label>
            <input
              type="date"
              value={filtrosValores.fecha_hasta}
              onChange={(e) => setFiltro('fecha_hasta', e.target.value)}
              placeholder="Hasta"
            />
          </>
        )}
        {filtroVisible('cuenta') && (
          <div style={{ position: 'relative' }}>
            <button
              type="button"
              onClick={() => setAbiertoFiltroCuentaCaja(!abiertoFiltroCuentaCaja)}
              style={{
                minWidth: 180,
                height: 32,
                padding: '0 10px',
                textAlign: 'left',
                border: '1px solid #ccc',
                borderRadius: 4,
                background: '#fff',
                cursor: 'pointer',
              }}
            >
              {(() => {
                const sel = Array.isArray(filtrosValores.cuenta_o_caja) ? filtrosValores.cuenta_o_caja : [];
                if (sel.length === 0) return 'Todas las cuentas y cajas';
                const cajasSel = sel.filter(v => String(v).startsWith('caja_'));
                const cuentasSel = sel.filter(v => String(v).startsWith('cuenta_'));
                const nombres = [
                  ...cajasSel.map(v => cajas.find(c => `caja_${c.id}` === v)?.nombre || v),
                  ...cuentasSel.map(v => {
                    const cb = cuentasBancarias.find(c => `cuenta_${c.id}` === v);
                    return cb ? `${cb.numero_cuenta}${cb.banco_nombre ? ` (${cb.banco_nombre})` : ''}` : v;
                  }),
                ];
                return sel.length === 0 ? 'Todas' : `${sel.length} seleccionada(s): ${nombres.slice(0, 2).join(', ')}${nombres.length > 2 ? '...' : ''}`;
              })()}
            </button>
            {abiertoFiltroCuentaCaja && (
              <>
                <div
                  style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9998 }}
                  onClick={() => setAbiertoFiltroCuentaCaja(false)}
                />
                <div
                  style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    marginTop: 4,
                    background: '#fff',
                    border: '1px solid #ccc',
                    borderRadius: 8,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    padding: '12px 16px',
                    maxHeight: 320,
                    overflowY: 'auto',
                    zIndex: 9999,
                    minWidth: 280,
                  }}
                >
                  <div style={{ marginBottom: 8, fontWeight: 600, fontSize: 12, color: '#555' }}>Cajas</div>
                  {cajas.map(c => {
                    const val = `caja_${c.id}`;
                    const sel = Array.isArray(filtrosValores.cuenta_o_caja) ? filtrosValores.cuenta_o_caja : [];
                    const checked = sel.includes(val);
                    return (
                      <label key={val} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, cursor: 'pointer' }}>
                        <input type="checkbox" checked={checked} onChange={() => toggleCuentaOCaja(val)} />
                        <span>{c.nombre || `Caja ${c.id}`}</span>
                      </label>
                    );
                  })}
                  <div style={{ marginTop: 12, marginBottom: 8, fontWeight: 600, fontSize: 12, color: '#555' }}>Cuentas bancarias</div>
                  {cuentasBancarias.map(cb => {
                    const val = `cuenta_${cb.id}`;
                    const sel = Array.isArray(filtrosValores.cuenta_o_caja) ? filtrosValores.cuenta_o_caja : [];
                    const checked = sel.includes(val);
                    return (
                      <label key={val} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, cursor: 'pointer' }}>
                        <input type="checkbox" checked={checked} onChange={() => toggleCuentaOCaja(val)} />
                        <span>{cb.numero_cuenta} {cb.banco_nombre ? `- ${cb.banco_nombre}` : ''}</span>
                      </label>
                    );
                  })}
                  <button
                    type="button"
                    onClick={() => setAbiertoFiltroCuentaCaja(false)}
                    style={{ marginTop: 12, padding: '6px 12px', fontSize: 12 }}
                  >
                    Cerrar
                  </button>
                </div>
              </>
            )}
          </div>
        )}
        {filtroVisible('tipo') && (
          <select
            value={filtrosValores.tipo}
            onChange={(e) => setFiltro('tipo', e.target.value)}
          >
            <option value="">Todos (ingreso / egreso)</option>
            <option value="ingreso">Ingreso</option>
            <option value="egreso">Egreso</option>
          </select>
        )}
        {filtroVisible('medio_pago') && (
          <select
            value={filtrosValores.forma_pago_id}
            onChange={(e) => setFiltro('forma_pago_id', e.target.value)}
            style={{ minWidth: 200 }}
          >
            <option value="">Todos los medios de pago</option>
            {formasPago.map(fp => (
              <option key={fp.id} value={fp.id}>{fp.nombre || `Forma ${fp.id}`}</option>
            ))}
          </select>
        )}
        {filtroVisible('usuario') && (
          <select
            value={filtrosValores.usuario_id}
            onChange={(e) => setFiltro('usuario_id', e.target.value)}
            style={{ minWidth: 180 }}
          >
            <option value="">Todos los usuarios</option>
            {usuarios.map(u => (
              <option key={u.id} value={u.id}>{u.nombre || u.usuario || `Usuario ${u.id}`}</option>
            ))}
          </select>
        )}
      </div>
      <div className="filters-toolbar filters-toolbar--acciones movimientos-fondos-toolbar">
          <button
            type="button"
            className="btn-action-icon"
            onClick={limpiarFiltros}
            title="Limpiar todos los filtros"
          >
            <span aria-hidden="true">🧹</span>
          </button>
          {hasPermission('crear_movimientos_fondos') && (
            <>
              <Link
                to="/movimientos/nuevo"
                state={{ modoFondos: true, tipoFondos: 'ingreso' }}
                className="btn-fondo-accion btn-fondo-deposito"
                title="Depósito de fondos"
              >
                <span className="btn-fondo-icon" aria-hidden="true">📥</span>
                <span>Depósito</span>
              </Link>
              <Link
                to="/movimientos/nuevo"
                state={{ modoFondos: true, tipoFondos: 'egreso' }}
                className="btn-fondo-accion btn-fondo-extraccion"
                title="Extracción de fondos"
              >
                <span className="btn-fondo-icon" aria-hidden="true">📤</span>
                <span>Extracción</span>
              </Link>
              <Link
                to="/movimientos/nuevo"
                state={{ modoFondos: true, clase_movimiento: 'transferencia_caja' }}
                className="btn-fondo-accion btn-fondo-transferencia"
                title="Transferencia entre cajas"
              >
                <span className="btn-fondo-icon" aria-hidden="true">↔</span>
                <span>Transf. cajas</span>
              </Link>
              <Link
                to="/movimientos/nuevo"
                state={{ modoFondos: true, clase_movimiento: 'transferencia_cuenta' }}
                className="btn-fondo-accion btn-fondo-transferencia"
                title="Transferencia entre cuentas"
              >
                <span className="btn-fondo-icon" aria-hidden="true">⇄</span>
                <span>Transf. cuentas</span>
              </Link>
            </>
          )}
          <SelectorColumnas
            columnas={COLUMNAS_DEFINICION}
            visibleIds={Object.keys(columnasVisibles).filter(id => columnasVisibles[id])}
            ordenColumnas={ordenColumnas}
            onOrdenChange={setOrdenColumnas}
            onToggle={(id) => setColumnasVisibles(prev => ({ ...prev, [id]: !prev[id] }))}
            onRestaurar={() => setColumnasVisibles({ ...defaultColumnasVisibles })}
            onSave={guardarPreferenciasListado}
            titulo="Columnas visibles"
            labelBoton="Columnas"
          />
          <SelectorFiltros
            filtros={FILTROS_DEFINICION}
            visibleIds={Object.keys(filtrosVisibles).filter(id => filtrosVisibles[id])}
            onToggle={(id) => setFiltrosVisibles(prev => ({ ...prev, [id]: !prev[id] }))}
            onRestaurar={() => setFiltrosVisibles({ ...defaultFiltrosVisibles })}
            onSave={guardarPreferenciasListado}
            titulo="Filtros visibles"
            labelBoton="Filtros"
          />
          {movimientosConSaldoAcumulado.length > 0 && (
            <ExportActions
              onCsv={() => handleExportListado('csv')}
              onExcel={() => handleExportListado('excel')}
              onPrint={generarPDFListado}
              printLabel="PDF"
              disabled={loading || refrescando}
            />
          )}
      </div>

      {hayFiltrosActivos && (saldoFiltrado !== null || saldoCuenta !== null) && (
        <div className="saldo-resumen">
          {extractoEntidadUnica ? (
            <>
              {saldoFiltrado !== null && (
                <div>
                  <strong>Saldo inicial del extracto</strong>:{' '}
                  <span style={{ fontWeight: 600, color: Number(saldoFiltrado) < 0 ? '#e74c3c' : '#111' }}>
                    ${Number(saldoFiltrado).toLocaleString('es-AR', { minimumFractionDigits: 2 })}
                  </span>
                </div>
              )}
              {saldoCuenta !== null && (
                <div>
                  <strong>Saldo actual</strong>:{' '}
                  <span style={{ fontWeight: 600, color: Number(saldoCuenta) < 0 ? '#e74c3c' : '#111' }}>
                    ${Number(saldoCuenta).toLocaleString('es-AR', { minimumFractionDigits: 2 })}
                  </span>
                </div>
              )}
              <p style={{ margin: '6px 0 0', fontSize: 12, color: '#666' }}>
                La columna Saldo es corrido en orden cronológico (antiguo → reciente). Ingreso suma y egreso resta.
                Entre dos filas visibles puede haber otros movimientos. Rojo solo si el saldo es menor a 0.
              </p>
            </>
          ) : (
            <>
              {saldoFiltrado !== null && (
                <div>
                  <strong>Saldo de los movimientos filtrados</strong>:{' '}
                  <span style={{ fontWeight: 600, color: Number(saldoFiltrado) < 0 ? '#e74c3c' : '#111' }}>
                    ${Number(saldoFiltrado).toLocaleString('es-AR', { minimumFractionDigits: 2 })}
                  </span>
                </div>
              )}
              {saldoCuenta !== null && (
                <div>
                  <strong>Saldo acumulado de fondos (sin filtros)</strong>:{' '}
                  <span style={{ fontWeight: 600, color: Number(saldoCuenta) < 0 ? '#e74c3c' : '#111' }}>
                    ${Number(saldoCuenta).toLocaleString('es-AR', { minimumFractionDigits: 2 })}
                  </span>
                </div>
              )}
            </>
          )}
        </div>
      )}

      <div className="listado-panel-cuerpo clientes-listado-cuerpo">
      <div
        className={
          refrescando
            ? 'listado-tabla-area clientes-listado-tabla-area listado-tabla-area--refreshing clientes-listado-tabla-area--refreshing'
            : 'listado-tabla-area clientes-listado-tabla-area'
        }
      >
        {refrescando && (
          <div className="listado-refresh-overlay clientes-listado-refresh-overlay" aria-live="polite">
            <div className="listado-refresh-spinner clientes-listado-refresh-spinner" />
            <span className="listado-refresh-label clientes-listado-refresh-label">Actualizando…</span>
          </div>
        )}
      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              {ordenColumnasVisible.map(colId => (
                <th
                  key={colId}
                  className="th-sortable"
                  onClick={() => handleSortColumn(colId)}
                  title={`Ordenar por ${getColumnLabel(colId)}. Clic de nuevo invierte el orden.`}
                >
                  <span>{getColumnLabel(colId)}</span>
                  {sortByColumn === colId && (
                    <span className="sort-indicator" aria-hidden="true">
                      {sortAsc ? ' ▲' : ' ▼'}
                    </span>
                  )}
                </th>
              ))}
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {loading && movimientosConSaldoAcumulado.length === 0 ? (
              <tr>
                <td colSpan={ordenColumnasVisible.length + 1} className="no-data">
                  Cargando…
                </td>
              </tr>
            ) : movimientosConSaldoAcumulado.length === 0 ? (
              <tr>
                <td colSpan={ordenColumnasVisible.length + 1} className="no-data">
                  No hay movimientos de fondos que coincidan con el filtro
                </td>
              </tr>
            ) : (
              movimientosConSaldoAcumulado.map(mf => (
                <tr key={mf.id}>
                  {ordenColumnasVisible.map(colId => {
                    if (colId === 'fecha') {
                      return <td key={colId}>{formatFecha(mf.fecha)}</td>;
                    }
                    if (colId === 'cuenta_banco') {
                      const texto = textoOrigenDestino(mf);
                      return (
                        <td key={colId}>
                          <div style={{ fontWeight: '500', fontSize: '13px' }}>{texto}</div>
                        </td>
                      );
                    }
                    if (colId === 'tipo') {
                      const etiqueta = mf.clase_label || etiquetaClaseFondo(mf.clase_movimiento, mf.tipo);
                      const esIngreso = mf.tipo === 'ingreso';
                      return (
                        <td key={colId}>
                          <span className={`badge ${esIngreso ? 'credito' : 'debito'}`}>
                            {etiqueta}
                          </span>
                        </td>
                      );
                    }
                    if (colId === 'concepto') {
                      return <td key={colId}>{mf.concepto || '-'}</td>;
                    }
                    if (colId === 'ingreso') {
                      const credito = mf.credito != null ? parseFloat(mf.credito) : (mf.tipo === 'ingreso' ? parseFloat(mf.monto_total || 0) : 0);
                      return (
                        <td key={colId} className="positive" style={{ textAlign: 'right' }}>
                          {credito > 0 ? <strong>${credito.toLocaleString('es-AR', { minimumFractionDigits: 2 })}</strong> : '-'}
                        </td>
                      );
                    }
                    if (colId === 'egreso') {
                      const debito = mf.debito != null ? parseFloat(mf.debito) : (mf.tipo === 'egreso' ? parseFloat(mf.monto_total || 0) : 0);
                      return (
                        <td key={colId} className="negative" style={{ textAlign: 'right' }}>
                          {debito > 0 ? <strong>${debito.toLocaleString('es-AR', { minimumFractionDigits: 2 })}</strong> : '-'}
                        </td>
                      );
                    }
                    if (colId === 'saldo') {
                      const saldo = parseFloat(mf.saldo_acumulado || 0);
                      const colorSaldo = saldo < 0 ? '#e74c3c' : '#111';
                      return (
                        <td key={colId} style={{ textAlign: 'right', color: colorSaldo }}>
                          <strong style={{ color: colorSaldo }}>
                            ${saldo.toLocaleString('es-AR', { minimumFractionDigits: 2 })}
                          </strong>
                        </td>
                      );
                    }
                    if (colId === 'medio_pago') {
                      return <td key={colId}>{getMedioPagoLabel(mf)}</td>;
                    }
                    if (colId === 'usuario') {
                      return <td key={colId}>{mf.usuario_nombre || '-'}</td>;
                    }
                    if (colId === 'detalles') {
                      const det = mf.detalles || [];
                      const resumen = det.length === 0 ? '-' : det.length === 1
                        ? (det[0].tipo === 'caja' ? `Caja: ${det[0].caja_nombre || '—'}` : det[0].tipo === 'cuenta' ? 'Cuenta' : `Cheque ${det[0].cheque_numero || ''}`) + ` — $${parseFloat(det[0].monto || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}`
                        : `${det.length} ítems`;
                      return <td key={colId} style={{ fontSize: '12px', color: '#555' }}>{resumen}</td>;
                    }
                    return <td key={colId}>-</td>;
                  })}
                  <td>
                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'nowrap' }}>
                      <button
                        type="button"
                        onClick={() => abrirDetalleFondo(mf)}
                        className="btn-action-icon"
                        title="Ver detalles del movimiento"
                      >
                        <span aria-hidden="true">🔍</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => imprimirResumenMovimientoFondo(mf, formatFecha)}
                        className="btn-action-icon"
                        title="Imprimir comprobante"
                      >
                        <span aria-hidden="true">🖨️</span>
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      </div>
      </div>
      </div>

      {detalleFondo && (
        <div
          className="modal-overlay"
          onClick={cerrarDetalleFondo}
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
          }}
        >
          <div
            className="modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: 'white', padding: '24px', borderRadius: '8px', maxWidth: '620px', width: '95%', maxHeight: '90vh', overflow: 'auto',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ margin: 0 }}>Detalle del movimiento de fondos</h3>
              <button type="button" onClick={cerrarDetalleFondo} style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer' }}>×</button>
            </div>
            <table className="data-table" style={{ width: '100%', marginBottom: '16px' }}>
              <tbody>
                <tr><td style={{ fontWeight: '600', width: '140px' }}>Fecha</td><td>{formatFecha(detalleFondo.fecha)}</td></tr>
                <tr><td style={{ fontWeight: '600' }}>Tipo</td><td><span className={`badge ${detalleFondo.tipo === 'ingreso' ? 'credito' : 'debito'}`}>{detalleFondo.clase_label || etiquetaClaseFondo(detalleFondo.clase_movimiento, detalleFondo.tipo)}</span></td></tr>
                <tr><td style={{ fontWeight: '600' }}>Concepto</td><td>{detalleFondo.concepto || '-'}</td></tr>
                <tr><td style={{ fontWeight: '600' }}>Monto</td><td className={detalleFondo.tipo === 'ingreso' ? 'positive' : 'negative'}><strong>${parseFloat(detalleFondo.monto_total || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}</strong></td></tr>
                {detalleFondo.forma_pago_nombre && (
                  <tr><td style={{ fontWeight: '600' }}>Esta línea</td><td>{detalleFondo.forma_pago_nombre}</td></tr>
                )}
                <tr><td style={{ fontWeight: '600' }}>Origen</td><td>{detalleFondo.origen_label || textoOrigenDestino(detalleFondo).split(' → ')[0] || '-'}</td></tr>
                <tr><td style={{ fontWeight: '600' }}>Destino</td><td>{detalleFondo.destino_label || textoOrigenDestino(detalleFondo).split(' → ')[1] || '-'}</td></tr>
                <tr><td style={{ fontWeight: '600' }}>Usuario</td><td>{detalleFondo.usuario_nombre || '-'}</td></tr>
              </tbody>
            </table>
            <div style={{ borderTop: '1px solid #eee', paddingTop: '12px' }}>
              <strong style={{ display: 'block', marginBottom: '8px' }}>Detalle ítems (origen/destino)</strong>
              {(detalleFondo.detalles || []).length === 0 ? (
                <span style={{ color: '#888' }}>Sin detalle</span>
              ) : (
                <ul style={{ margin: 0, paddingLeft: '20px' }}>
                  {(detalleFondo.detalles || []).map((d, i) => (
                    <li key={i} style={{ marginBottom: '4px' }}>
                      {d.tipo === 'caja' && (<>Caja: {d.caja_nombre || '—'} — ${parseFloat(d.monto || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}</>)}
                      {d.tipo === 'cheque_tercero' && (<>Cheque Nº {d.cheque_numero || '-'} — ${parseFloat(d.monto || d.cheque_importe || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}</>)}
                      {d.tipo === 'cuenta' && (<>Cuenta — ${parseFloat(d.monto || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}</>)}
                    </li>
                  ))}
                </ul>
              )}
            </div>
            {(detalleFondo.comprobante || detalleFondo.pago_id || detalleFondo.recibo_cobro_id || detalleFondoLoading || detalleFondoError) && (
              <div style={{ borderTop: '1px solid #eee', paddingTop: '12px', marginTop: '12px' }}>
                {detalleFondo.comprobante && (
                  <>
                    <strong style={{ display: 'block', marginBottom: '8px' }}>Comprobante vinculado</strong>
                    <p style={{ margin: '0 0 8px', fontSize: '14px' }}>
                      <strong>{detalleFondo.comprobante.tipo} N° {detalleFondo.comprobante.numero}</strong>
                      {detalleFondo.comprobante.entidad && (
                        <span style={{ display: 'block', color: '#555', marginTop: '4px' }}>{detalleFondo.comprobante.entidad}</span>
                      )}
                      {detalleFondo.comprobante.monto != null && (
                        <span style={{ display: 'block', color: '#555' }}>
                          Total comprobante: ${parseFloat(detalleFondo.comprobante.monto || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}
                        </span>
                      )}
                    </p>
                    {(detalleFondo.movimientos_vinculados || []).length > 1 && (
                      <div style={{ marginBottom: '12px', fontSize: '13px', color: '#555' }}>
                        Este comprobante generó {detalleFondo.movimientos_vinculados.length} movimientos de fondos (una por forma de pago):
                        <ul style={{ margin: '6px 0 0', paddingLeft: '20px' }}>
                          {detalleFondo.movimientos_vinculados.map((v) => (
                            <li key={v.id} style={{ marginBottom: '2px' }}>
                              #{v.id} · {v.forma_pago_nombre || 'Forma de pago'} · ${parseFloat(v.monto_total || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}
                              {v.id === detalleFondo.id ? ' (esta línea)' : ''}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    <FormasPagoDetalleList
                      formasPago={detalleFondo.comprobante.formas_pago}
                      titulo="Formas de pago del comprobante"
                      loading={detalleFondoLoading}
                      error={detalleFondoError}
                      formatFecha={formatFecha}
                    />
                  </>
                )}
                {!detalleFondo.comprobante && detalleFondoLoading && (
                  <p style={{ color: '#7f8c8d', margin: 0 }}>Cargando comprobante vinculado...</p>
                )}
                {!detalleFondo.comprobante && !detalleFondoLoading && detalleFondoError && (
                  <p style={{ color: '#c0392b', margin: 0, fontSize: '14px' }}>{detalleFondoError}</p>
                )}
              </div>
            )}
            <div style={{ marginTop: 16, display: 'flex', gap: 8 }}>
              <button
                type="button"
                className="btn-primary"
                onClick={() => imprimirResumenMovimientoFondo(detalleFondo, formatFecha)}
              >
                Imprimir comprobante
              </button>
              <button type="button" className="btn-secondary" onClick={cerrarDetalleFondo}>
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MovimientosFondos;
